import { injectable } from 'inversify';

import { GridCard, GridItem } from '../../types';

// Константы для работы с сеткой
const CARD_SIZE_M = 2;
const CARD_SIZE_S = 1;

@injectable()
export class GridService {
  /**
   * Проверяет, является ли элемент сетки карточкой (не null и не 'occupied')
   */
  isGridCard(item: GridItem | undefined): item is GridCard {
    return (
      item !== null &&
      item !== undefined &&
      item !== 'occupied' &&
      typeof item === 'object'
    );
  }

  /**
   * Проверяет, является ли элемент сетки 'occupied'
   */
  isOccupied(item: GridItem | undefined): item is 'occupied' {
    return item === 'occupied';
  }

  /**
   * Проверяет, является ли элемент сетки пустым (null)
   */
  isEmpty(item: GridItem | undefined): item is null {
    return item === null;
  }

  /**
   * Удаляет дубликаты из массива на основе ключа, сохраняя первый элемент
   */
  deduplicateByKey<T, K extends keyof T>(items: T[], key: K): T[] {
    const seen = new Set<T[K]>();
    return items.filter((item) => {
      if (seen.has(item[key])) {
        return false;
      }
      seen.add(item[key]);
      return true;
    });
  }

  /**
   * Вычисляет размер карточки в ячейках сетки
   */
  private getServiceSize(service: GridCard): number {
    return service.size === 'M' ? CARD_SIZE_M : CARD_SIZE_S;
  }

  /**
   * Нормализует сетку: извлекает карточки и расставляет их правильно с учетом размеров сетки.
   * Автоматически добавляет 'occupied' для M-карточек и null если карточка не помещается в строку.
   * Дополняет сетку до кратности размеру страниц.
   */
  normalizeGrid(
    gridItems: GridItem[],
    gridCols: number,
    selectedSize: number,
  ): GridItem[] {
    const cards = this.convertGridItemsToCards(gridItems);
    const itemsPerPage = selectedSize * gridCols;

    // Если нет карточек, создаем одну пустую страницу
    if (cards.length === 0) {
      return Array(itemsPerPage).fill(null);
    }

    const result: GridItem[] = [];
    let currentRowPosition = 0;

    for (const card of cards) {
      const cardSize = this.getServiceSize(card);
      const spaceLeftInRow = gridCols - currentRowPosition;

      // Если карточка не помещается в текущей строке, переходим на новую
      if (cardSize > spaceLeftInRow) {
        result.push(...Array(spaceLeftInRow).fill(null));
        currentRowPosition = 0;
      }

      // Добавляем карточку
      result.push(card);
      currentRowPosition++;

      // Для M-карточки добавляем 'occupied'
      if (card.size === 'M') {
        result.push('occupied');
        currentRowPosition++;
      }

      // Сбрасываем позицию если заполнили строку
      if (currentRowPosition >= gridCols) {
        currentRowPosition = 0;
      }
    }

    // Дополняем текущую строку до конца
    if (currentRowPosition > 0) {
      result.push(...Array(gridCols - currentRowPosition).fill(null));
    }

    // Дополняем до полных страниц
    // Если последняя страница полностью заполнена - добавляем еще одну пустую страницу
    const currentCells = result.length;
    const isLastPageFull =
      currentCells % itemsPerPage === 0 && currentCells > 0;
    const totalCellsNeeded = isLastPageFull
      ? currentCells + itemsPerPage
      : Math.ceil(currentCells / itemsPerPage) * itemsPerPage;

    result.push(...Array(totalCellsNeeded - currentCells).fill(null));

    return result;
  }

  /**
   * Вставляет карточку в gridItems по индексу и нормализует сетку
   */
  insertCardAtIndex(
    gridItems: GridItem[],
    card: GridCard,
    index: number,
    gridCols: number,
    selectedSize: number,
  ): GridItem[] {
    const cards = this.convertGridItemsToCards(gridItems);

    const insertIndex = gridItems
      .slice(0, index)
      .filter((item): item is GridCard => this.isGridCard(item)).length;

    cards.splice(insertIndex, 0, card);

    return this.normalizeGrid(cards, gridCols, selectedSize);
  }

  /**
   * Удаляет карточку из gridItems по индексу и нормализует сетку
   */
  removeCardAtIndex(
    gridItems: GridItem[],
    index: number,
    gridCols: number,
    selectedSize: number,
  ): GridItem[] {
    const card = gridItems[index];

    if (!this.isGridCard(card)) {
      return gridItems;
    }

    const cards = this.convertGridItemsToCards(gridItems).filter(
      (c) => c.id !== card.id,
    );

    return this.normalizeGrid(cards, gridCols, selectedSize);
  }

  /**
   * Находит индекс карточки по ID (игнорируя 'occupied')
   */
  findCardIndex(gridItems: GridItem[], cardId: string): number {
    return gridItems.findIndex(
      (item) => this.isGridCard(item) && item.id === cardId,
    );
  }

  /**
   * Конвертирует GridItem[] обратно в массив карточек (без null и 'occupied')
   * Используется для отправки данных на бэкенд
   */
  convertGridItemsToCards(gridItems: GridItem[]): GridCard[] {
    return gridItems.filter((item): item is GridCard => this.isGridCard(item));
  }

  /**
   * Находит индекс последнего сервиса в массиве gridItems (игнорируя 'occupied')
   */
  private findLastServiceIndex(gridItems: GridItem[]): number {
    for (let i = gridItems.length - 1; i >= 0; i--) {
      const item = gridItems[i];
      if (this.isGridCard(item)) {
        return i;
      }
    }
    return -1;
  }

  /**
   * Вычисляет номер строки по индексу
   */
  private getRowFromIndex(index: number, gridCols: number): number {
    return Math.floor(index / gridCols);
  }

  /**
   * Корректирует targetIndex для M-карточек
   */
  private adjustTargetIndexForMCard(
    targetIndex: number,
    gridCols: number,
  ): number {
    const targetRow = Math.floor(targetIndex / gridCols);
    const targetCol = targetIndex % gridCols;
    const lastColIndex = gridCols - 1;
    const columnsNeededForMCard = 2;
    const penultimateColIndex = gridCols - columnsNeededForMCard;

    let startCol: number;
    if (targetCol >= lastColIndex) {
      startCol = penultimateColIndex;
    } else if (targetCol === 0) {
      startCol = 0;
    } else {
      startCol = targetCol;
    }

    return targetRow * gridCols + startCol;
  }

  /**
   * Вычисляет общее количество страниц на основе gridServices
   */
  calculateTotalPages(
    gridServices: GridItem[],
    selectedSize: number,
    gridCols: number,
  ): number {
    // Вычисляем общее количество строк в сетке
    const totalRows = Math.ceil(gridServices.length / gridCols);

    // Если нет строк, возвращаем 1 страницу
    if (totalRows === 0) return 1;

    // Вычисляем количество страниц (округляем вверх)
    return Math.ceil(totalRows / selectedSize);
  }

  /**
   * Проверяет, есть ли карточки в сетке
   */
  hasCards(gridItems: GridItem[]): boolean {
    return this.convertGridItemsToCards(gridItems).length > 0;
  }

  /**
   * Возвращает массив ID карточек из сетки
   */
  getCardIds(gridItems: GridItem[]): string[] {
    return this.convertGridItemsToCards(gridItems).map((card) => card.id);
  }

  /**
   * Создает временную сетку без указанной карточки
   * Используется для проверки доступности ячеек при перетаскивании из сетки
   */
  getTempGridWithoutCard(
    gridItems: GridItem[],
    cardId: string,
    gridCols: number,
    selectedSize: number,
  ): GridItem[] {
    const cards = this.convertGridItemsToCards(gridItems).filter(
      (card) => card.id !== cardId,
    );
    return this.normalizeGrid(cards, gridCols, selectedSize);
  }

  /**
   * Проверяет, можно ли вставить карточку в указанную ячейку
   */
  canDropServiceAtCell(
    cellIndex: number,
    gridItems: GridItem[],
    gridCols: number,
    card: GridCard,
    isNewCard = false,
  ): boolean {
    // Ячейка должна быть пустой (null)
    if (gridItems[cellIndex] !== null) {
      return false;
    }

    // Для M-карточки проверяем, что следующая ячейка тоже пустая и в той же строке
    if (card.size === 'M') {
      const targetRow = this.getRowFromIndex(cellIndex, gridCols);
      const nextCellIndex = cellIndex + 1;
      const nextCellRow = this.getRowFromIndex(nextCellIndex, gridCols);

      // Проверяем, что следующая ячейка в той же строке и она пустая
      if (
        nextCellRow !== targetRow ||
        nextCellIndex >= gridItems.length ||
        gridItems[nextCellIndex] !== null
      ) {
        return false;
      }
    }

    // Находим индекс последней карточки
    const lastServiceIndex = this.findLastServiceIndex(gridItems);

    // Если сетка пустая, можно вставить только в первую ячейку
    if (lastServiceIndex === -1) {
      return cellIndex === 0;
    }

    // Находим первую пустую ячейку после последней карточки
    let firstEmptyAfterLast = lastServiceIndex + 1;
    // Для M-карточек нужно пропустить 'occupied'
    while (
      firstEmptyAfterLast < gridItems.length &&
      gridItems[firstEmptyAfterLast] === 'occupied'
    ) {
      firstEmptyAfterLast++;
    }

    if (isNewCard) {
      // Для новых карточек - можно и в последнюю пустую
      return cellIndex <= firstEmptyAfterLast;
    } else {
      // Для существующих карточек - только между карточками, НЕ в последнюю пустую
      return cellIndex < firstEmptyAfterLast;
    }
  }

  /**
   * Изменяет размер карточки и нормализует сетку.
   * При S-\>M карточка остается на строке, соседи сдвигаются вниз.
   */
  changeCardSize(
    gridItems: GridItem[],
    updatedCard: GridCard,
    gridCols: number,
    selectedSize: number,
  ): GridItem[] {
    const cardIndex = this.findCardIndex(gridItems, updatedCard.id);
    const currentCard = gridItems[cardIndex];

    if (cardIndex === -1 || !this.isGridCard(currentCard)) return gridItems;
    if (currentCard.size === updatedCard.size) return gridItems;

    let cards = this.convertGridItemsToCards(gridItems);
    cards = cards.map((c) => (c.id === updatedCard.id ? updatedCard : c));

    if (updatedCard.size === 'M') {
      cards = this.reorderCardsForMSize(
        cards,
        gridItems,
        cardIndex,
        updatedCard,
        gridCols,
      );
    }

    return this.normalizeGrid(cards, gridCols, selectedSize);
  }

  /**
   * Переупорядочивает карточки при расширении до M-размера.
   * Карточки на той же строке, пересекающиеся с M-карточкой, сдвигаются после неё.
   */
  private reorderCardsForMSize(
    cards: GridCard[],
    gridItems: GridItem[],
    cardIndex: number,
    updatedCard: GridCard,
    gridCols: number,
  ): GridCard[] {
    const row = this.getRowFromIndex(cardIndex, gridCols);
    const col = cardIndex % gridCols;
    const mStart = col === gridCols - 1 ? col - 1 : col;
    const mEnd = mStart + 1;

    const toMove = gridItems
      .map((item, i) => ({
        item,
        col: i % gridCols,
        row: this.getRowFromIndex(i, gridCols),
      }))
      .filter(({ item, col: c, row: r }) => {
        if (!this.isGridCard(item) || item.id === updatedCard.id || r !== row) {
          return false;
        }
        const end = c + this.getServiceSize(item) - 1;
        return c <= mEnd && end >= mStart;
      })
      .map(({ item }) => item as GridCard);

    if (toMove.length === 0) return cards;

    const toMoveIds = new Set(toMove.map((c) => c.id));
    const filtered = cards.filter((c) => !toMoveIds.has(c.id));
    const idx = filtered.findIndex((c) => c.id === updatedCard.id);
    filtered.splice(idx + 1, 0, ...toMove);

    return filtered;
  }

  /**
   * Перемещает карточку в новую позицию и нормализует сетку
   */
  moveServiceToPosition(
    serviceId: string,
    targetIndex: number,
    gridItems: GridItem[],
    gridCols: number,
    selectedSize: number,
  ): GridItem[] {
    const cards = this.convertGridItemsToCards(gridItems);
    const sourceCardIndex = cards.findIndex((c) => c.id === serviceId);
    if (sourceCardIndex === -1) return gridItems;

    const movedCard = cards[sourceCardIndex];

    // Корректируем targetIndex для M-карточек
    const adjustedTargetIndex =
      movedCard.size === 'M'
        ? this.adjustTargetIndexForMCard(targetIndex, gridCols)
        : targetIndex;

    // Удаляем карточку и создаем временную сетку без неё
    cards.splice(sourceCardIndex, 1);
    const tempGrid = this.normalizeGrid(cards, gridCols, selectedSize);

    // Вычисляем позицию вставки - количество карточек до adjustedTargetIndex
    const insertIndex = tempGrid
      .slice(0, adjustedTargetIndex)
      .filter((item): item is GridCard => this.isGridCard(item)).length;

    // Вставляем карточку и нормализуем финальную сетку
    cards.splice(insertIndex, 0, movedCard);
    return this.normalizeGrid(cards, gridCols, selectedSize);
  }
}
